"use client";

import { type ReactNode, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import {
  SUBSTANCE_STRINGS,
  SubstanceFields,
  SubstanceProblem,
} from "@/components/tools/chemistry/substance-fields";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type Amounts,
  amountsFrom,
  type KnownQuantity,
} from "@/lib/tool-kit/chemistry/amounts";
import { AVOGADRO } from "@/lib/tool-kit/chemistry/elements";
import {
  fewestSigFigs,
  formatPlain,
  formatSig,
  type NumberFormatter,
  scientific,
} from "@/lib/tool-kit/chemistry/format";
import { chargeText, formulaToUnicode } from "@/lib/tool-kit/chemistry/formula";
import {
  resolveSubstance,
  type Substance,
  type SubstanceMode,
} from "@/lib/tool-kit/chemistry/substance";
import {
  AMOUNT,
  findUnit,
  fromBase,
  MASS,
  toBase,
  unitOptions,
} from "@/lib/tool-kit/chemistry/units";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const GRAMS_TO_MOLES_CONVERTER_STRINGS = {
  ...SUBSTANCE_STRINGS,
  knownLabel: "I know the",
  knownMass: "Mass",
  knownAmount: "Amount (moles)",
  knownParticles: "Number of particles",
  valueMass: "Mass",
  valueAmount: "Amount of substance",
  valueParticles: "Number of particles (molecules, formula units or atoms)",
  valueHintParticles: "Write 6.02e23 or 6.02 × 10^23 for large counts.",
  massUnit: "Mass unit",
  amountUnit: "Amount unit",
  sigFigs: "Significant figures in the answer",
  sigMatch: "Match my data",
  sigChoice:
    "one: {count} significant figure|other: {count} significant figures",
  results: "Results",
  mass: "Mass",
  amount: "Amount of substance",
  particles: "Particles",
  molarMassOfFormula: "Molar mass of {formula}: M = {molar} g/mol",
  molarMassGiven: "Molar mass: M = {molar} g/mol",
  working: "The working",
  unitStep: "{value} {unit} = {base} {baseUnit}",
  gramsToMoles: "{grams} g × (1 mol / {molar} g) = {moles} mol",
  molesToGrams: "{moles} mol × ({molar} g / 1 mol) = {grams} g",
  molesToParticles:
    "{moles} mol × ({avogadro} particles / 1 mol) = {particles} particles",
  particlesToMoles:
    "{particles} particles × (1 mol / {avogadro} particles) = {moles} mol",
  guardNote:
    "Intermediate results carry one extra digit; only the final answers are rounded to {sig}.",
  atomsHeading: "Atoms of each element in the sample",
  columnElement: "Element",
  columnPerUnit: "Atoms per formula unit",
  columnInSample: "Atoms in the sample",
  enterValue: "Enter the amount you know to see the conversion.",
  negative:
    "Enter a value of zero or more: a mass, an amount or a count cannot be negative.",
  charge: "Charge {charge} does not change the molar mass.",
  copy: "Copy results",
  copied: "Copied!",
  copyText: "{mass} {massUnit} = {amount} {amountUnit} = {particles} particles",
} as const;

type Strings = typeof GRAMS_TO_MOLES_CONVERTER_STRINGS;

const SIG_CHOICES = ["3", "4", "5", "6"] as const;
const DEFAULT_SIG = 4;
const MASS_OPTIONS = unitOptions(MASS).filter((o) =>
  ["ug", "mg", "g", "kg"].includes(o.value)
);
const AMOUNT_OPTIONS = unitOptions(AMOUNT);

interface Inputs {
  known: KnownQuantity;
  valueText: string;
  massUnit: string;
  amountUnit: string;
}

/** The known value in base units (g, mol or a count), or null if it is not a usable number. */
function knownBase(inputs: Inputs): number | null {
  const value = parseMeasuredNumber(inputs.valueText);
  if (value === null) {
    return null;
  }
  if (inputs.known === "mass") {
    return toBase(value, MASS, inputs.massUnit);
  }
  if (inputs.known === "amount") {
    return toBase(value, AMOUNT, inputs.amountUnit);
  }
  return value;
}

function workingLines(
  strings: Strings,
  fmt: NumberFormatter,
  known: KnownQuantity,
  amounts: Amounts,
  molar: number,
  sig: number
): string[] {
  const guard = sig + 1;
  const common = {
    molar: formatPlain(fmt, molar),
    avogadro: scientific(fmt, AVOGADRO, 9),
  };
  if (known === "mass") {
    return [
      t(strings.gramsToMoles, {
        ...common,
        grams: formatPlain(fmt, amounts.grams),
        moles: formatSig(fmt, amounts.moles, guard),
      }),
      t(strings.molesToParticles, {
        ...common,
        moles: formatSig(fmt, amounts.moles, guard),
        particles: formatSig(fmt, amounts.particles, sig),
      }),
    ];
  }
  if (known === "amount") {
    return [
      t(strings.molesToGrams, {
        ...common,
        moles: formatPlain(fmt, amounts.moles),
        grams: formatSig(fmt, amounts.grams, sig),
      }),
      t(strings.molesToParticles, {
        ...common,
        moles: formatPlain(fmt, amounts.moles),
        particles: formatSig(fmt, amounts.particles, sig),
      }),
    ];
  }
  return [
    t(strings.particlesToMoles, {
      ...common,
      particles: formatPlain(fmt, amounts.particles),
      moles: formatSig(fmt, amounts.moles, guard),
    }),
    t(strings.molesToGrams, {
      ...common,
      moles: formatSig(fmt, amounts.moles, guard),
      grams: formatSig(fmt, amounts.grams, sig),
    }),
  ];
}

function unitStepLine(
  strings: Strings,
  fmt: NumberFormatter,
  inputs: Inputs,
  base: number,
  sig: number
): string | null {
  const isMass = inputs.known === "mass";
  const unitId = isMass ? inputs.massUnit : inputs.amountUnit;
  const baseId = isMass ? "g" : "mol";
  if (inputs.known === "particles" || unitId === baseId) {
    return null;
  }
  const family = isMass ? MASS : AMOUNT;
  const typed = parseMeasuredNumber(inputs.valueText) ?? 0;
  return t(strings.unitStep, {
    value: formatPlain(fmt, typed),
    unit: findUnit(family, unitId).symbol,
    base: formatSig(fmt, base, sig + 1),
    baseUnit: baseId,
  });
}

function AtomsTable({
  substance,
  particles,
  sig,
  strings,
  fmt,
}: {
  substance: Extract<Substance, { kind: "ok" }>;
  particles: number;
  sig: number;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  if (!substance.breakdown) {
    return null;
  }
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.atomsHeading}</h3>
      <DataTable
        head={[
          strings.columnElement,
          strings.columnPerUnit,
          strings.columnInSample,
        ]}
        rows={substance.breakdown.rows.map((row) => [
          `${row.name} (${row.symbol})`,
          formatPlain(fmt, row.count),
          formatSig(fmt, particles * row.count, sig),
        ])}
      />
    </div>
  );
}

function ConversionResults({
  substance,
  inputs,
  base,
  sig,
}: {
  substance: Extract<Substance, { kind: "ok" }>;
  inputs: Inputs;
  base: number;
  sig: number;
}) {
  const strings = useToolUi(GRAMS_TO_MOLES_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const amounts = amountsFrom(inputs.known, base, substance.molarMass);
  const massSymbol = findUnit(MASS, inputs.massUnit).symbol;
  const amountSymbol = findUnit(AMOUNT, inputs.amountUnit).symbol;
  const massText = formatSig(
    fmt,
    fromBase(amounts.grams, MASS, inputs.massUnit),
    sig
  );
  const amountText = formatSig(
    fmt,
    fromBase(amounts.moles, AMOUNT, inputs.amountUnit),
    sig
  );
  const particlesText = formatSig(fmt, amounts.particles, sig);
  const lines = workingLines(
    strings,
    fmt,
    inputs.known,
    amounts,
    substance.molarMass,
    sig
  );
  const step = unitStepLine(strings, fmt, inputs, base, sig);
  const charge = substance.formula?.charge ?? 0;
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis={inputs.known !== "mass"}
          label={strings.mass}
          value={`${massText} ${massSymbol}`}
        />
        <StatCard
          emphasis={inputs.known !== "amount"}
          label={strings.amount}
          value={`${amountText} ${amountSymbol}`}
        />
        <StatCard
          emphasis={inputs.known !== "particles"}
          label={strings.particles}
          value={particlesText}
        />
      </StatGrid>
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>
          {substance.formula
            ? t(strings.molarMassOfFormula, {
                formula: formulaToUnicode(substance.formula),
                molar: formatPlain(fmt, substance.molarMass),
              })
            : t(strings.molarMassGiven, {
                molar: formatPlain(fmt, substance.molarMass),
              })}
        </p>
        {step ? <p>{step}</p> : null}
        {lines.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p className="font-sans text-xs">
          {t(strings.guardNote, {
            sig: tPlural(strings.sigChoice, locale, sig, {
              count: fmt(sig),
            }).trim(),
          })}
        </p>
      </div>
      {charge === 0 ? null : (
        <Notice>{t(strings.charge, { charge: chargeText(charge) })}</Notice>
      )}
      <AtomsTable
        fmt={fmt}
        particles={amounts.particles}
        sig={sig}
        strings={strings}
        substance={substance}
      />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          mass: massText,
          massUnit: massSymbol,
          amount: amountText,
          amountUnit: amountSymbol,
          particles: particlesText,
        })}
      />
    </>
  );
}

function valueLabel(strings: Strings, known: KnownQuantity): string {
  if (known === "mass") {
    return strings.valueMass;
  }
  return known === "amount" ? strings.valueAmount : strings.valueParticles;
}

export default function GramsToMolesConverterWidget() {
  const strings = useToolUi(GRAMS_TO_MOLES_CONVERTER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<SubstanceMode>("formula");
  const [formula, setFormula] = useState("NaCl");
  const [molarMassText, setMolarMassText] = useState("");
  const [known, setKnown] = useState<KnownQuantity>("mass");
  const [valueText, setValueText] = useState("25.0");
  const [massUnit, setMassUnit] = useState("g");
  const [amountUnit, setAmountUnit] = useState("mol");
  const [sigChoice, setSigChoice] = useState<string>("match");

  const substance = resolveSubstance(mode, formula, molarMassText);
  const inputs: Inputs = { known, valueText, massUnit, amountUnit };
  const base = knownBase(inputs);
  const matched = fewestSigFigs(
    mode === "mass" ? [valueText, molarMassText] : [valueText],
    DEFAULT_SIG
  );
  const sig = sigChoice === "match" ? matched : Number(sigChoice);

  let body: ReactNode;
  if (substance.kind !== "ok") {
    body = <SubstanceProblem strings={strings} substance={substance} />;
  } else if (valueText.trim() === "" || base === null) {
    body = <Notice>{strings.enterValue}</Notice>;
  } else if (base < 0) {
    body = <ErrorNotice>{strings.negative}</ErrorNotice>;
  } else {
    body = (
      <ConversionResults
        base={base}
        inputs={inputs}
        sig={sig}
        substance={substance}
      />
    );
  }

  return (
    <>
      <SubstanceFields
        formula={formula}
        formulaPlaceholder="NaCl"
        id="gm"
        mode={mode}
        molarMass={molarMassText}
        molarMassPlaceholder="58.44"
        onFormulaChange={setFormula}
        onModeChange={setMode}
        onMolarMassChange={setMolarMassText}
        strings={strings}
      />
      <PillGroup
        label={strings.knownLabel}
        name="gm-known"
        onChange={setKnown}
        options={[
          { value: "mass", label: strings.knownMass },
          { value: "amount", label: strings.knownAmount },
          { value: "particles", label: strings.knownParticles },
        ]}
        value={known}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={known === "particles" ? strings.valueHintParticles : undefined}
          id="gm-value"
          label={valueLabel(strings, known)}
          numeric
          onChange={setValueText}
          value={valueText}
        />
        <SelectField
          id="gm-sig"
          label={strings.sigFigs}
          onChange={setSigChoice}
          options={[
            { value: "match", label: strings.sigMatch },
            ...SIG_CHOICES.map((value) => ({
              value,
              label: tPlural(strings.sigChoice, locale, Number(value), {
                count: fmt(Number(value)),
              }).trim(),
            })),
          ]}
          value={sigChoice}
        />
        <SelectField
          id="gm-mass-unit"
          label={strings.massUnit}
          onChange={setMassUnit}
          options={MASS_OPTIONS}
          value={massUnit}
        />
        <SelectField
          id="gm-amount-unit"
          label={strings.amountUnit}
          onChange={setAmountUnit}
          options={AMOUNT_OPTIONS}
          value={amountUnit}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}
